/**
 * Background videos of extra.json at chart time t (W1-08; formats-and-packaging.md P§5.3, ARCHITECTURE.md §7.3
 * step 2):
 *
 *  - timing: a video starts at chart time sec(time) (extra.json tempo); it is drawn while t - start >= 0 with
 *    `localSec = t - start` (seconds into the file). Phira shows it only while localSec < its duration and never
 *    loops: the duration is a property of the decoded file, so the renderer stops drawing at the end of the file;
 *  - alpha (default 1) and dim (default 0): fixed, Phira's empty Default anim (null -> the default), or keyframes
 *    with (0 s, default, hold) prepended when they do not start at beat 0 (no extrapolation);
 *  - unattached videos: full viewport (`m` null), white tint (no intro/outro fade);
 *  - attached videos (`attach.line`, an export index): the attach quirk - only the line's OWN transform (own
 *    translation and own rotation; parents, scale and the line's alpha are ignored) applied in Phira's y-down
 *    camera space, so the video appears mirrored vertically and rotates the other way than its line; tinted by the
 *    line's colour events, or without them by the live judge-line colour (the perfect colour, alpha x res.alpha).
 *    An index outside the chart (Phira panics) draws nothing.
 */

import { Mat2 } from '../../math/mat2d';
import type { Sec, TempoMap } from '../../time/types';
import type { IrExtraJson, IrVideo } from '../../ir/types';
import type { VideoId } from '../../model/types';
import type { RGBAf } from '../../util/types';
import type { EngineCompileContext, FrameDrawListBuilder, FrameOptions, VideoDraw } from '../types';
import type { AssetResolver } from './effectsEval';
import { compileVar, varAt, type CompiledVar } from './extraAnim';

export interface CompiledVideo {
  readonly id: VideoId;
  /** Package path of the video file. */
  readonly path: string;
  readonly startSec: Sec;
  readonly scale: IrVideo['scale'];
  readonly alpha: CompiledVar;
  readonly dim: CompiledVar;
  /** Export index of the attached line, or null. */
  readonly attach: number | null;
}

const WHITE: RGBAf = [1, 1, 1, 1];

function compileVideoVar(v: IrVideo['alpha'], tempo: TempoMap, def: number): CompiledVar {
  if (v === undefined) return { kind: 'none' };
  return compileVar(v, (t) => tempo.secAtTriple(t), def);
}

export function compileVideo(v: IrVideo, index: number, tempo: TempoMap): CompiledVideo {
  return {
    id: v.srcVideo ?? (index as VideoId),
    path: v.path,
    startSec: tempo.secAtTriple(v.time),
    scale: v.scale,
    alpha: compileVideoVar(v.alpha, tempo, 1),
    dim: compileVideoVar(v.dim, tempo, 0),
    attach: v.attach?.line ?? null,
  };
}

export function compileVideos(extra: IrExtraJson, tempo: TempoMap): CompiledVideo[] {
  return extra.videos.map((v, i) => compileVideo(v, i, tempo));
}

/** Scalar value of a video variable at t. */
function scalarAt(v: CompiledVar, t: Sec, fallback: number): number {
  const value = varAt(v, t, fallback);
  return typeof value === 'number' ? value : (value[0] ?? fallback);
}

/** Fills `out.videos` with the videos playing at t. */
export function fillVideos(
  ctx: Pick<EngineCompileContext, 'lineOrder' | 'lineState'>,
  videos: readonly CompiledVideo[],
  t: Sec,
  opts: Pick<FrameOptions, 'skin' | 'resAlpha'>,
  resolve: AssetResolver | null,
  out: Pick<FrameDrawListBuilder, 'videos'>,
): void {
  for (const v of videos) {
    const localSec = t - v.startSec;
    if (!(localSec >= 0)) continue;
    const asset = resolve === null ? null : resolve(v.path);
    if (asset === null) continue;
    let m: VideoDraw['m'] = null;
    let tint = WHITE;
    if (v.attach !== null) {
      const line = ctx.lineOrder()[v.attach];
      if (line === undefined) continue;
      const st = ctx.lineState(line, t);
      m = Mat2.rotateDeg(
        Mat2.translate(Mat2.identity(), st.ownX, st.ownY, [0, 0, 0, 0, 0, 0]),
        st.ownRot,
        [0, 0, 0, 0, 0, 0],
      );
      if (st.color !== null) tint = [st.color[0], st.color[1], st.color[2], 1];
      else {
        const c = opts.skin.colorPerfect;
        tint = [c[0], c[1], c[2], c[3] * opts.resAlpha];
      }
    }
    out.videos.push({
      video: v.id,
      asset,
      localSec,
      scale: v.scale,
      m,
      tint,
      alpha: scalarAt(v.alpha, t, 1),
      dim: scalarAt(v.dim, t, 0),
    });
  }
}
